import "./Virtualizer.scss";
import React from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import clsx from "clsx";
import { DocumentedComponent } from "../../../shared/components/DocumentedComponent";
import { getDataProps, WithDataProps } from "../../../shared/utilities";

export type VirtualizerProps<TItem> = WithDataProps<{
    direction?: "row" | "column";
    items: Array<TItem>;
    renderItem: (item: TItem, rootProps: Record<string, unknown>, index: number) => React.ReactNode;
    estimateSize: (item: TItem, index: number) => number;
    overscan?: number;
    gap?: number;
    className?: string;
    [key: string]: any;
}>;

const Virtualizer: React.FC<VirtualizerProps<any>> = (props) => {
    const {
        direction = "column",
        items = [],
        renderItem,
        estimateSize: _estimateSize,
        overscan = 1,
        gap = 16,
        className,
    } = props;

    const parentRef = React.useRef<HTMLDivElement>(null);

    const estimateSize = React.useCallback(
        (index: number) => _estimateSize ? _estimateSize(items[index], index) : 100,
        [_estimateSize, items]
    );

    const virtualizer = useVirtualizer({
        horizontal: direction === "row",
        count: items?.length ?? 0,
        getScrollElement: () => parentRef.current,
        estimateSize,
        overscan,
        gap,
    });

    return (
        <div
            ref={parentRef}
            className={clsx("Virtualizer-root", className)}
            {...getDataProps(props)}
        >
            <div
                className={clsx("Virtualizer-inner", `direction-${direction}`)}
                style={{
                    height: direction === "column" ? virtualizer.getTotalSize() : "100%",
                    width: direction === "row" ? virtualizer.getTotalSize() : "100%",
                }}
            >
                {
                    virtualizer.getVirtualItems().map(virtualItem => renderItem(
                        items?.[virtualItem.index],
                        {
                            style: {
                                position: "absolute",
                                top: 0,
                                left: 0,
                                width: direction === "column" ? "100%" : undefined,
                                height: direction === "row" ? "100%" : undefined,
                                transform: `translate${direction === "column" ? "Y" : "X"}(${virtualItem.start}px)`
                            },
                            key: virtualItem.key,
                            "data-index": virtualItem.index,
                            ref: virtualizer.measureElement, // Invoke the `measureElement` function on item render to allow for dynamic item sizes
                        },
                        virtualItem.index
                    ))
                }
            </div>
        </div>
    );
}

(Virtualizer as DocumentedComponent).metadata = {
    description: "The `Virtualizer` component optimizes the rendering of large lists.  It does so by only rendering items which are currently visible within the scroll window (+/- a configurable overscan amount).  When scrolling occurs, items are dynamically added/removed from the DOM, as required.",
    attributes: [
        { name: "direction", type: "string", exactType: `"row" | "column"`, description: "The direction in which the list items should flow.  For `row`, items are laid out from left to right and horizontal scrolling occurs.  For `column`, items are laid out from top to bottom and vertical scrolling occurs.  Optional - defaults to \"column\"." },
        { name: "items", type: "object", exactType: "Array<any>", description: "The list of items to render (on demand).  The individual entries can be of any type - primitives, objects, arrays (for nested lists), etc." },
        { name: "renderItem", type: "function", exactType: "(item: any, rootProps: Record<string, any>, index: number) => React.ReactNode", template: "renderItem={(item, rootProps, index) => <div {...rootProps}>$1</div>}", description: "The function invoked to return the JSX/HTML content for the provided item.  This is invoked on demand, as the item is scrolled into view.  The `item` argument contains the specific item (from the `items` prop) to render.  The `rootProps` argument contains the default props which should be spread/applied to the root element.  **This is very important, as it includes the `style` field, which contains the actual position of the DOM element.  Failure to do so will result in all items being rendered on top of each other, in the top left corner of the container.**" },
        { name: "estimateSize", type: "function", exactType: "(item: any, index: number) => number", template: "estimateSize={(item, index) => $1}", description: "The callback function used to estimate the item's height (for `column` direction) or width (for `row` direction).  The returned value does not need to match the final item dimensions exactly, but if there is a large discrepancy between the estimated size and the actual size, the scroll bar may jump around as the user scrolls within the container.  When in doubt, it is best to return a value greater than the actual width/height here." },
        { name: "overscan", type: "number", description: "The number of items to pre-render on either side of the current viewport.  This reduces flickering during the scroll operation and prevents blank space from being visible when the estimated item dimensions do not match the final result.  Optional - defaults to 1." },
        { name: "gap", type: "number", description: "The spacing (in pixels) to apply between list items.  Optional - defaults to 16." },
        { name: "className", type: "string", description: "The (optional) CSS classes to apply to the root element." },
    ]
};

export { Virtualizer };